import React, { useEffect, useRef, useState } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import esriConfig from '@arcgis/core/config';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import '@arcgis/core/assets/esri/themes/light/main.css';

const MapComponent = () => {
  const mapDiv = useRef(null);
  const viewRef = useRef(null);
  const [popup, setPopup] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [selectedBasemap, setSelectedBasemap] = useState('arcgis/streets');
  const [showBasemapSelector, setShowBasemapSelector] = useState(false);
  const [showLegend, setShowLegend] = useState(true);

  
  const powerPlantData = [
    { name: "Kathu", longitude: 23.0278338, latitude: -27.6109354, province: "Northern Cape", technology: "Solar", capacity: "81.0", offtaker: "Eskom", status: "In Operation" },
    { name: "Kruisvallei", longitude: 28.3655017460431, latitude: -28.4047240488278, province: "Free State", technology: "Hydro", capacity: "4", offtaker: "Eskom", status: "In Operation" },
    { name: "Roggeveld", longitude: 20.5333149, latitude: -32.919701, province: "Western Cape", technology: "Wind", capacity: "147.0", offtaker: "Eskom", status: "In Operation" },
    { name: "Brandvalley", longitude: 20.439998, latitude: -32.976747, province: "Western Cape", technology: "Wind", capacity: "144.0", offtaker: "Eskom", status: "In Operation" },
    { name: "Rietkloof", longitude: 20.503073, latitude: -33.024993, province: "Western Cape", technology: "Wind", capacity: "144.0", offtaker: "Eskom", status: "In Operation" },
    { name: "Wolf", longitude: 24.922656, latitude: -33.257125, province: "Eastern Cape", technology: "Wind", capacity: "85.0", offtaker: "Eskom", status: "In Construction" },
    { name: "Virginia", longitude: 26.994764, latitude: -28.214213, province: "Free State", technology: "Solar", capacity: "275.0", offtaker: "Eskom", status: "In Construction"},
    { name: "Witberg", longitude: 20.461228, latitude: -33.287642, province: "Western Cape", technology: "Wind", capacity: "108", offtaker: "Sibanye-Stillwater", status: "In Construction"},
    { name: "Overberg", longitude: 20.353191, latitude: -34.145979, province: "Western Cape", technology: "Wind", capacity: "400", offtaker: "RBM/Rio Tinto & Discovery Green", status: "In Construction"},
    { name: "Mothership Rooftop Solar", longitude: 18.4255475, latitude: -33.9103192, province: "Western Cape", technology: "Solar", capacity: "1", offtaker: "City of Cape Town", status: "In Construction"},

  ];

  const basemapOptions = [
    { id: 'arcgis/streets', name: 'Streets', icon: '🏙️' },
    { id: 'arcgis/satellite', name: 'Satellite', icon: '🛰️' },
    // { id: 'arcgis/hybrid', name: 'Hybrid', icon: '🗺️' },
    { id: 'arcgis/terrain', name: 'Terrain', icon: '⛰️' },
    { id: 'arcgis/outdoor', name: 'Outdoor', icon: '🌲' },
    { id: 'arcgis/topographic', name: 'Topographic', icon: '📊' },
    { id: 'arcgis/navigation', name: 'Navigation', icon: '🧭' },
    { id: 'arcgis/dark-gray', name: 'Dark Gray', icon: '🌑' },
    { id: 'arcgis/light-gray', name: 'Light Gray', icon: '⚪' },
    { id: 'arcgis/oceans', name: 'Oceans', icon: '🌊' },
    { id: 'arcgis/imagery', name: 'imagery', icon: '🌍' },
    { id: 'arcgis/nova', name: 'Nova', icon: '@' }
  ];

  
  const getTechnologyIcon = (technology) => {
    const iconMap = {
      'Wind': '/wind.png',
      'Solar': '/solar.png',
      'Hydro': '/hydro.png'
    };
    return iconMap[technology] || '/default.png';
  };

  const getPopupColor = (technology) => {
    const colorMap = {
      'Wind': '#C22630',
      'Solar': '#efbb36',
      'Hydro': '#6ac7bd'
    };
    return colorMap[technology] || '#C22630';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'In Operation': '/operation.png',
      'In Construction': '/construction.png',
      'PTFC': '/ptfc.png'
    };
    return iconMap[status] || '/operation.png';
  };

  const getStatusIcon_2 = (status) => {
    const iconMap = {
      'In Operation': '/operation_v2.png',
      'In Construction': '/construction_v2.png',
      'PTFC': '/ptfc_v2.png'
    };
    return iconMap[status] || '/operation.png';
  };

  const getStatusCounts = () => {
    const counts = {
      'In Operation': 0,
      'In Construction': 0
    };
    powerPlantData.forEach(plant => {
      if (counts.hasOwnProperty(plant.status)) {
        counts[plant.status]++;
      }
    });
    return counts;
  };

  const changeBasemap = (basemapId) => {
    if (viewRef.current) {
      viewRef.current.map.basemap = basemapId;
      setSelectedBasemap(basemapId);
      setShowBasemapSelector(false);
    }
  };

  useEffect(() => {
    if (mapDiv.current) {
      const apiKey = process.env.REACT_APP_ARCGIS_API_KEY;
      
      if (!apiKey || apiKey === '') {
        console.error("ArcGIS API key missing! Please add API key to .env file.");
        return;
      }
      
      esriConfig.apiKey = apiKey;
      
      const map = new Map({
        basemap: selectedBasemap 
      });

      const view = new MapView({
        container: mapDiv.current,
        map: map,
        center: [24.0, -29.0], 
        zoom: 6
      });

      viewRef.current = view;

      view.when(() => {
        console.log("Map loaded successfully!");
        
        powerPlantData.forEach(plant => {
          const point = new Point({
            longitude: plant.longitude,
            latitude: plant.latitude
          });

          const technologySymbol = new PictureMarkerSymbol({
            url: getTechnologyIcon(plant.technology),
            width: "32px",
            height: "32px"
          });

          const statusSymbol = new PictureMarkerSymbol({
            url: getStatusIcon_2(plant.status),
            width: "18px",
            height: "18px",
            xoffset: 10,
            yoffset: 10
          });

          const technologyGraphic = new Graphic({
            geometry: point,
            symbol: technologySymbol,
            attributes: plant
          });

          const statusGraphic = new Graphic({
            geometry: point,
            symbol: statusSymbol,
            attributes: plant
          });

          view.graphics.add(technologyGraphic);
          view.graphics.add(statusGraphic);
        });

        view.on("click", (event) => {
          view.hitTest(event).then((response) => {
            if (response.results.length > 0) {
              const graphic = response.results[0].graphic;
              if (graphic.attributes) {
                const screenPoint = view.toScreen(graphic.geometry);
                setPopupPosition({ x: screenPoint.x, y: screenPoint.y });
                setPopup(graphic.attributes);
              }
            } else {
              setPopup(null);
            }
          });
        });
        
      }).catch((error) => {
        console.error("Error loading map:", error);
      });

      return () => {
        if (view) {
          view.destroy();
        }
      };
    }
  }, [selectedBasemap]);

  useEffect(() => {
    if (popup && viewRef.current) {
      const updatePopupPosition = () => {
        const point = new Point({
          longitude: popup.longitude,
          latitude: popup.latitude
        });
        const screenPoint = viewRef.current.toScreen(point);
        setPopupPosition({ x: screenPoint.x, y: screenPoint.y });
      };

      updatePopupPosition();

      const extentHandle = viewRef.current.watch("extent", updatePopupPosition);
      const scaleHandle = viewRef.current.watch("scale", updatePopupPosition);
      const rotationHandle = viewRef.current.watch("rotation", updatePopupPosition);

      return () => {
        extentHandle?.remove();
        scaleHandle?.remove();
        rotationHandle?.remove();
      };
    }
  }, [popup]);

  const StatusLegend = () => {
    const statusCounts = getStatusCounts();
    
    return (
      <div style={{
        position: 'absolute',
        top: '100px',
        right: '10px',
        zIndex: 1001,
        background: 'rgba(0, 0, 0, 0.6)',
        border: '2px solid #ddd',
        borderRadius: '12px',
        alignItems: 'center',
        padding: '15px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        fontFamily: 'Arial, sans-serif',
        minWidth: '200px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px'
        }}>
          <h4 style={{ margin: 0, color: '#ffffff', fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', alignItems: 'center' }}>
            Project Status
          </h4>
          <button
            onClick={() => setShowLegend(!showLegend)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)',
              color: '#666'
            }}
          >
            {showLegend ? '−' : '+'}
          </button>
        </div>
        
        {showLegend && (
          <div>
            {Object.entries(statusCounts).map(([status, count]) => (
              <div key={status} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '10px 0',
              }}>
                <img 
                  src={getStatusIcon(status)} 
                  alt={status}
                  style={{ width: '20px', height: '20px' }}
                />
                <span style={{ flex: 1, color: '#ffffff', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>{status}</span>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: '#c22630',
                  background: '#fff5f5',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: 'clamp(0.8rem, 1.5vw, 1rem)'
                }}>
                  {count}
                </span>
              </div>
            ))}
            <div style={{
              marginTop: '10px',
              padding: '8px',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <strong style={{ color: '#cccccc', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>
                Total: {powerPlantData.length} Plants
              </strong>
            </div>
          </div>
        )}
      </div>
    );
  };

  const BasemapSelector = () => (
    <div style={{
      position: 'absolute',
      top: '20px',
      right: '20px',
      zIndex: 1005,
      fontFamily: 'Arial, sans-serif'
    }}>
      <button
        onClick={() => setShowBasemapSelector(!showBasemapSelector)}
        className="basemap-selector-button"
        style={{
          background: '#000000',
          border: '2px solid #ddd',
          borderRadius: '8px',
          padding: '10px 15px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          fontSize: 'clamp(1rem, 2vw, 1.4rem)',
          // fontWeight: 'bold',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <span>🗺️</span>
        Map Style
        <span style={{ transform: showBasemapSelector ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          ▼
        </span>
      </button>

      {showBasemapSelector && (
        <div className="basemap-dropdown">
          <div className="basemap-dropdown-header">
            Choose Map Style
          </div>
          {basemapOptions.map((basemap) => (
            <button
              key={basemap.id}
              onClick={() => changeBasemap(basemap.id)}
              className={`basemap-option ${selectedBasemap === basemap.id ? 'selected' : ''}`}
            >
              <span style={{ fontSize: 'clamp(1rem, 2vw, 1.4rem)' }}>{basemap.icon}</span>
              <span>{basemap.name}</span>
              {selectedBasemap === basemap.id && (
                <span style={{ marginLeft: 'auto', color: '#c22630' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const CustomPopup = ({ plant, position, onClose }) => (
    <div 
      style={{
        position: 'absolute',
        left: position.x - 180,
        top: position.y - 400,
        background: getPopupColor(plant.technology),
        border: `2px solid ${getPopupColor(plant.technology)}`,
        borderRadius: '5px',
        padding: '10px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        zIndex: 1000,
        minWidth: '50px',
        fontFamily: 'Arial, sans-serif',
        transform: 'translateZ(0)',
        pointerEvents: 'auto'
      }}
    >
      <div style={{
        position: 'absolute',
        bottom: '-10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderTop: `10px solid ${getPopupColor(plant.technology)}`
      }} />
      
      <button 
        onClick={onClose}
        style={{
          position: 'absolute',
          right: '8px',
          top: '8px',
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)',
          cursor: 'pointer',
          color: '#ffffff',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ×
      </button>
      
      <div className="power-plant-popup">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <img 
            src={getStatusIcon(plant.status)} 
            alt={plant.technology}
            style={{ width: '32px', height: '32px', alignItems: 'center' }}
          />
          <h3 style={{ 
            alignItems: 'center',
            color: '#ffffff', 
            fontSize: 'clamp(1.3rem, 2.6vw, 2rem)'
          }}>
            {plant.name}
          </h3>
        </div>
        
        <div className="popup-details" style={{color: '#ffffff'}}>
          <p style={{ margin: '8px 0', lineHeight: '1.4', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>
            <strong style={{ color: '#ffffff' }}>📍 Location:</strong> {plant.province}
          </p>
          <p style={{ margin: '8px 0', lineHeight: '1.4', display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>
            <strong style={{ color: '#ffffff' }}>📊 Status:</strong> 
            <img 
              src={getStatusIcon(plant.status)} 
              alt={plant.status}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ 
              color: '#ffffff',
              fontWeight: 'bold'
            }}>
              {plant.status}
            </span>
          </p>
          <p style={{ margin: '8px 0', lineHeight: '1.4', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>
            <strong style={{ color: '#ffffff' }}>⚡ Capacity:</strong> 
            <span style={{ color: '#ffffff', fontWeight: 'bold', fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)', marginLeft: '5px' }}>
              {plant.capacity} MW
            </span>
          </p>
          <p style={{ margin: '8px 0', lineHeight: '1.4', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>
            <strong style={{ color: '#ffffff' }}>🔧 Technology:</strong> {plant.technology}
          </p>
          {plant.offtaker && (
            <p style={{ margin: '8px 0', lineHeight: '1.4', fontSize: 'clamp(0.9rem, 1.8vw, 1.2rem)' }}>
              <strong style={{ color: '#ffffff' }}>🏢 Off-taker:</strong> {plant.offtaker}
            </p>
          )}
          
          <div style={{ 
            marginTop: '15px', 
            fontSize: 'clamp(0.8rem, 1.5vw, 1rem)', 
            color: '#ffffff',
            borderTop: '1px solid rgba(255,255,255,0.3)',
            paddingTop: '10px'
          }}>
            <strong>🌐 Coordinates:</strong> {plant.longitude}, {plant.latitude}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'relative', height: 'calc(100vh - 80px)', width: '100%' }}>
      <div ref={mapDiv} style={{ height: '100%', width: '100%' }} />
      
      <BasemapSelector />
      <StatusLegend />
      
      {popup && (
        <CustomPopup 
          plant={popup} 
          position={popupPosition}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  );
};

export default MapComponent; 